<script>
  import Popover from '#lib/Popover.svelte';
  import { fetch_entries, to_plain_text } from '#lib/util.js';
  import t from '#lib/i18n.svelte.js';
  import TextInput from '#lib/TextInput.svelte';
  import FormLabel from '#lib/FormLabel.svelte';
  import ImageEntryModel from '#lib/ImageEntryModel.svelte.js';
  import { lazy } from '#lib/lazy.svelte.js';
  const entry_picker_drawer = lazy(() => import('#lib/EntryPickerDrawer.svelte'));
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import Image from '#lib/Image.svelte';
  import Switch from '#lib/Switch.svelte';
  import { page } from '$app/state';
  import { getContext } from 'svelte';

  const MAX_PREVIEW_IMAGES = 3;

  let { entry } = $props();
  let site_settings = getContext('site_settings');
  // The home page is always findable; the server ignores the flag on it too
  let is_home_page = $derived(site_settings.home_page_preview_ref == entry.entry_id);
  let anchor_ref = $state();
  let popover_ref = $state();
  let image_picker_open = $state(false);

  let preview_images = $derived(entry.preview_images.length > 0 ? entry.preview_images : entry.preview.images);

  $effect.pre(() => {
    const multiref = entry.preview_images_multiref;
    // Skip fetch when local preview_images already match multiref (e.g. after local splice)
    const current_ids = entry.preview_images?.map(img => img.entry_id) ?? [];
    if (multiref.length === 0 || JSON.stringify(multiref) === JSON.stringify(current_ids)) return;
    fetch_entries(multiref).then(images => {
      entry.preview_images = images.map(image => new ImageEntryModel(image));
    });
  });
</script>

{#if page.data.current_user?.role === 'admin'}
  <button
    class='preview-tool-trigger flex-row gap-x g-3' 
    bind:this={anchor_ref} 
    popovertarget='preview_tool_popover_{entry.entry_id}' 
    title={t('preview_card')}
  >
    <Image 
      {...preview_images[0]} 
      width={28} 
      height={28} 
      checkerboard={true}
      aspect_ratio={1}
      sizes="28px"
    />
    <!-- A fixed label instead of the entry's title: the title changes while typing, and a toolbar
         whose buttons move under the pointer is how the delete button gets hit by accident -->
    <ButtonLabel variant='brand ghostly text-compact'>{t('preview')}</ButtonLabel>
  </button>

  <Popover
    anchor={anchor_ref}
    id='preview_tool_popover_{entry.entry_id}'
    bind:ref={popover_ref}
    placement='bottom-start'
    class='normal'
  >
    <div class='preview-tool-content flex-column'>
      
      <div class='share-settings flex-column gap-y g-3 py p-4'>

        <!-- Visibility lives in the dedicated toolbar button (EntryVisibilityTool) -->

        <div class='flex-column'>
          <FormLabel for='entry_preview_preamble'>{t('preamble')}</FormLabel>
          <TextInput 
            id='entry_preview_preamble' 
            bind:value={entry.preview_preamble} 
            placeholder={to_plain_text(entry.preview.preamble) ?? ''}
          />
        </div>
      
        <div class='flex-column'>
          <FormLabel for='entry_preview_title'>{t('title')}</FormLabel>
          <TextInput 
            id='entry_preview_title' 
            bind:value={entry.preview_title} 
            placeholder={to_plain_text(entry.preview.title) ?? ''}
          />
        </div>
        
        <div class='flex-column'>
          <FormLabel for='entry_preview_description'>{t('description')}</FormLabel>
          <TextInput 
            id='entry_preview_description' 
            bind:value={entry.preview_description} 
            placeholder={to_plain_text(entry.preview.description) ?? ''}
            multiline={true}
          />
        </div>

        <!-- Show all preview images in admin mode -->
        {#if entry.type !== 'image'}
          <div class='flex-column gap-y g-2'>
            <FormLabel>{t('preview_images')}</FormLabel>
            {#if entry.preview_images_multiref?.length > 0}
              {#each entry.preview_images as image, index (entry.preview_images_multiref[index])}
                <div class='preview-image-row flex-row items-center gap-x g-2'>
                  <!-- display_* (not width/height, the natural size) also sizes videos, whose
                       full-width wrapper otherwise pushes the remove button out of the popover -->
                  <Image
                    {...image}
                    display_width={48}
                    display_height={48}
                    aspect_ratio={1}
                    sizes="48px"
                  />
                  <!-- Cards swap to the second picture while pointed at (StoryBlock: HOVER IMAGE) -->
                  {#if index === 1}
                    <span class='text-control text-2'>{t('shown_on_hover')}</span>
                  {/if}
                  <button
                    onclick={() => {
                      entry.preview_images_multiref.splice(index, 1);
                      entry.preview_images.splice(index, 1);
                    }}
                    title={t('clear_selection')}
                  >
                    <ButtonLabel variant='brand subtle-outlined' start_icon='close' />
                  </button>
                </div>
              {/each}
            {/if}
            {#if (entry.preview_images_multiref?.length ?? 0) < MAX_PREVIEW_IMAGES}
              <button onclick={() => { popover_ref?.hidePopover(); image_picker_open = true; }}>
                <ButtonLabel variant='brand subtle-outlined' start_icon='image'>
                  {t('browse_images')}
                </ButtonLabel>
              </button>
            {/if}
          </div>
        {/if}

        <!-- Off: left out of the site search, the sitemap and search engines (noindex) -->
        {#if !is_home_page}
          <Switch
            variant='brand flush'
            value={!entry.exclude_from_search}
            toggle={(value) => { entry.exclude_from_search = !value; }}
          >
            {t('show_in_search')}
          </Switch>
        {/if}

      </div>

    </div>
  </Popover>

  {#if image_picker_open && entry_picker_drawer.component}
    {@const remaining_slots = MAX_PREVIEW_IMAGES - (entry.preview_images_multiref?.length ?? 0)}
    <entry_picker_drawer.component
      entry_type='image'
      excluded_ids={entry.preview_images_multiref}
      multi_select={remaining_slots > 1}
      confirm_label={t('add_image')}
      confirm_label_plural={t('add_selected_images', { count: '{{count}}' })}
      onconfirm={(entry_ids) => {
        const ids_to_add = entry_ids.slice(0, remaining_slots);
        for (const entry_id of ids_to_add) {
          entry.preview_images_multiref.push(entry_id);
        }
        image_picker_open = false;
      }}
      oncancel={() => { image_picker_open = false; }}
      initial_view='grid'
    />
  {/if}
{/if}

<style lang='postcss'>
  .preview-tool-trigger {
    /* Labels never wrap or hyphenate ("EINSTEL-LUNGEN") under toolbar squeeze */
    white-space: nowrap;
    hyphens: none;

    /* The thumbnail must never size the toolbar — images AND videos are
       pinned to the island height (the video wrapper has its own sizing
       logic and escaped the img-only max-height, inflating the whole bar). */
    :global(.media-item),
    :global(.video-wrapper) {
      width: var(--size-8);
      height: var(--size-8);
      max-height: var(--size-8);
      object-fit: cover;
      overflow: hidden;
      flex-shrink: 0;
    }
    :global(.video-wrapper video) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .preview-tool-content {
    max-width: min(40ch, 100vw - 2* var(--page-inline-spacing));
  }
  /* Remove buttons line up at the end whether or not a row carries a label */
  .preview-image-row > button {
    margin-inline-start: auto;
  }
  .share-settings {
    padding-inline: var(--input-inline-spacing);
  }
</style>